import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function NotFound() {
  const { t } = useTranslation('errors');

  return (
    <div className="min-h-screen bg-surface-light dark:bg-surface-dark flex items-center justify-center px-4 font-sans transition-colors duration-200">
      <div className="max-w-md w-full text-center animate-in fade-in zoom-in duration-700">
        {/* 404 Number */}
        <div className="mb-2">
          <h1 className="text-[12rem] font-black leading-none tracking-tighter text-slate-100 dark:text-slate-900 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none">
            404
          </h1>
          <div className="relative z-10">
            <div className="mx-auto w-24 h-24 bg-brand-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-brand-200 dark:shadow-none mb-8">
              <svg
                className="w-12 h-12 text-on-accent-light"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="relative z-10">
          <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
            {t('404.title')}
          </h2>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium mb-10 leading-relaxed">
            {t('404.description')}
          </p>

          {/* Helpful Suggestions */}
          <div className="bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark p-6 rounded-2xl shadow-soft mb-10">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-text-subtle-light dark:text-text-subtle-dark mb-4">
              {t('404.suggestionsTitle')}
            </p>
            <div className="grid grid-cols-1 gap-2">
              <SuggestionLink to="/dashboard" label={t('404.dashboard')} />
              <SuggestionLink to="/backups/configs" label={t('404.backups')} />
              <SuggestionLink to="/agents" label={t('404.agents')} />
            </div>
          </div>

          {/* Return to the public home page */}
          <Link
            to="/landing"
            className="inline-flex items-center px-8 py-3.5 bg-brand-600 text-on-accent-light text-sm font-black uppercase tracking-widest rounded-xl shadow-lg shadow-brand-200 dark:shadow-none hover:bg-brand-700 transition-all active:scale-95"
          >
            {t('404.backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}

function SuggestionLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between px-4 py-3 rounded-xl bg-surface-2-light dark:bg-surface-2-dark hover:bg-accent-soft-light dark:hover:bg-accent-soft-dark text-slate-700 dark:text-slate-300 hover:text-brand-600 dark:hover:text-brand-400 font-bold text-sm transition-all group"
    >
      <span>{label}</span>
      <svg
        className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
